import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Headphones, Mic, Send, Square, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Common";
import { Crisis, SubHeader, WbCard } from "@/components/wb/WbUi";
import { API_BASE, api } from "@/lib/api";
import { isMissing, wbError } from "@/lib/wb";
import { cn } from "@/lib/utils";

const STARTERS = ["I need some encouragement.", "I'm feeling overwhelmed.", "Help me rebuild my confidence.", "Help me understand what I can control.", "Remind me of my strengths.", "I want to reflect on my career journey.", "Give me some wisdom for what I'm going through.", "Help me recognize my progress."];
const txt = (m) => (typeof m === "string" ? m : m.text || m.content || "");
const mine = (m) => m.role === "user" || m.from === "user";

const useVoice = (sid, onText) => {
  const [rec, setRec] = useState(false);
  const r = useRef({});
  const stop = () => { const x = r.current; x.mr?.state === "recording" && x.mr.stop(); cancelAnimationFrame(x.raf); x.ctx?.close().catch(() => {}); setRec(false); };
  const start = async (autoStop) => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error("Microphone access is blocked", { description: "Click the lock icon in your browser's address bar, allow the microphone for this site, then try again.", duration: 8000 }); return; }
    const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported?.("audio/webm") ? { mimeType: "audio/webm" } : undefined);
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const fd = new FormData();
      fd.append("audio", new Blob(chunks, { type: "audio/webm" }), "voice.webm");
      fd.append("session_id", sid);
      try { const { data } = await api.post("/ai/transcribe", fd); onText((data.text || "").trim()); } catch (e) { wbError(e); }
    };
    r.current = { mr };
    mr.start();
    setRec(true);
    if (autoStop) {
      const ctx = new AudioContext(), an = ctx.createAnalyser(), buf = new Uint8Array(an.fftSize);
      ctx.createMediaStreamSource(stream).connect(an);
      let quiet = null, heard = false;
      const tick = () => {
        an.getByteTimeDomainData(buf);
        const lvl = Math.max(...buf.map((v) => Math.abs(v - 128)));
        if (lvl > 10) { heard = true; quiet = null; } else if (heard) { quiet ??= Date.now(); if (Date.now() - quiet > 1500) return stop(); }
        r.current.raf = requestAnimationFrame(tick);
      };
      r.current = { mr, ctx, raf: requestAnimationFrame(tick) };
    }
  };
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return { rec, start, stop };
};

export default function Companion() {
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hands, setHands] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  const endRef = useRef(null);
  const handsRef = useRef(false);
  handsRef.current = hands;
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (t, session = sid) => {
    if (!t || !session) return;
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/chat/session/${session}/message`, { text: t });
      let reply;
      if (Array.isArray(data.messages)) { setMsgs(data.messages); reply = txt(data.messages.at(-1)); }
      else { const x = data.reply || data.message || data; reply = txt(x); setMsgs((m) => [...m, { role: "assistant", text: reply }]); }
      if (handsRef.current && reply) listen(reply, true, session);
    } catch (e) { wbError(e); } finally { setBusy(false); }
  };
  const voice = useVoice(sid, (t) => { if (!t) return; if (handsRef.current) send(t); else setText((x) => (x ? `${x} ${t}` : t)); });

  const listen = async (t, thenMic, session = sid) => {
    try {
      setPlaying(t);
      const { data } = await api.post("/ai/speak", { text: t, session_id: session });
      const a = audio.current;
      a.src = `${API_BASE}/ai/speak/${encodeURIComponent(data.clip_id)}?sig=${encodeURIComponent(data.sig)}`;
      a.onended = () => { setPlaying(null); if (thenMic && handsRef.current) voice.start(true); };
      await a.play();
    } catch (e) { setPlaying(null); wbError(e); }
  };

  const begin = async (first) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat/companion/start", {});
      setSid(data.session_id); setMsgs(data.messages || []);
      if (first) { setBusy(false); await send(first, data.session_id); }
    } catch (e) { isMissing(e) ? toast("Your Career Companion arrives with the next RPOPRO update.", { description: "It will appear here automatically once it reaches your account." }) : wbError(e); } finally { setBusy(false); }
  };
  const submit = (e) => { e.preventDefault(); const t = text.trim(); if (!t || busy) return; setText(""); send(t); };

  return (
    <div className="space-y-6" data-testid="wb-companion">
      <SubHeader title="My Career Companion" subtitle="A calm space to talk through what's on your mind, by typing or speaking." />
      <audio ref={audio} className="hidden" data-testid="wb-companion-audio" />
      {!sid ? (
        <WbCard gold>
          <p className="mb-4 font-wb text-lg text-zinc-100">Where would you like to start?</p>
          <div className="flex flex-wrap gap-2">{STARTERS.map((s, i) => <button key={s} disabled={busy} onClick={() => begin(s)} className="rounded-full border border-[#D4AF37]/40 bg-black/20 px-3 py-1.5 text-sm text-zinc-200 transition-colors hover:border-[#D4AF37] hover:text-[#F3C649]" data-testid={`wb-starter-${i}`}>{s}</button>)}</div>
          <Button variant="ghost" className="mt-4" disabled={busy} onClick={() => begin()} data-testid="wb-companion-start">{busy ? <Spinner /> : "Or just start talking"}</Button>
        </WbCard>
      ) : (
        <div className="flex h-[65vh] flex-col rounded-[20px] border border-[#27272A] bg-[#121214]">
          <div className="flex items-center justify-end border-b border-[#27272A] px-4 py-2">
            <button onClick={() => { setHands(!hands); if (!hands) toast("Hands-free on", { description: "Replies are read aloud, then the mic opens. Pause for about 1.5 seconds to send." }); else voice.stop(); }} className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs", hands ? "border-[#D4AF37] bg-[#33290D] text-[#F3C649]" : "border-[#27272A] text-zinc-400")} aria-pressed={hands} data-testid="wb-handsfree-toggle"><Headphones className="h-3.5 w-3.5" />Hands-free</button>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5" data-testid="wb-companion-messages">
            {msgs.map((m, i) => (
              <div key={i} className={cn("flex", mine(m) ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed", mine(m) ? "bg-[#D4AF37] text-black" : "border border-[#27272A] bg-[#18181B] text-zinc-200")}>
                  <p className="whitespace-pre-wrap">{txt(m)}</p>
                  {!mine(m) && <button onClick={() => listen(txt(m))} className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#D4AF37]" data-testid={`wb-listen-${i}`}><Volume2 className="h-3.5 w-3.5" />{playing === txt(m) ? "Playing…" : "Listen"}</button>}
                </div>
              </div>
            ))}
            {busy && <p className="text-sm italic text-zinc-500">Thinking with you…</p>}
            <div ref={endRef} />
          </div>
          <form onSubmit={submit} className="flex gap-2 border-t border-[#27272A] p-3">
            <Button type="button" variant="outline" size="icon" onClick={voice.rec ? voice.stop : () => voice.start(hands)} className={voice.rec ? "animate-pulse border-red-500 text-red-400" : ""} aria-label={voice.rec ? "Stop recording" : "Speak"} data-testid="wb-mic-button">{voice.rec ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</Button>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={voice.rec ? "Listening…" : "Type or tap the mic…"} className="bg-[#09090B]" data-testid="wb-companion-input" />
            <Button type="submit" disabled={busy || !text.trim()} data-testid="wb-companion-send"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
      <Crisis />
    </div>
  );
}
